import { useEffect, useRef, useState } from "react";
import type { DraftStateDTO, RecentPick } from "../../shared/types";
import { managerColor } from "../lib/colors";
import { formatMoney } from "../lib/format";
import { DirectionTag } from "./ui";

let audioCtx: AudioContext | null = null;
function chime() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioCtx = audioCtx || new Ctx();
    const t = audioCtx.currentTime;
    [784, 1175].forEach((f, i) => {
      const o = audioCtx!.createOscillator();
      const g = audioCtx!.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      o.connect(g);
      g.connect(audioCtx!.destination);
      const start = t + i * 0.09;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.32);
      o.start(start);
      o.stop(start + 0.34);
    });
  } catch {
    /* audio unavailable */
  }
}

/** Shows a flourish banner when a new pick locks. Detected from recentPicks[0]. */
export function PickAnnounce({
  state,
  soundOn = false,
}: {
  state: DraftStateDTO;
  soundOn?: boolean;
}) {
  const last = state.recentPicks[0] ?? null;
  const seen = useRef<number | null>(null);
  const [show, setShow] = useState<RecentPick | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!last) return;
    if (seen.current === null) {
      seen.current = last.pickNumber; // skip the initial load
      return;
    }
    if (last.pickNumber !== seen.current) {
      seen.current = last.pickNumber;
      setShow(last);
      setNonce((n) => n + 1);
      if (soundOn) chime();
      const id = setTimeout(() => setShow(null), 3400);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [last?.pickNumber, soundOn]);

  if (!show) return null;
  const seed = state.managers.find((m) => m.name === show.manager)?.seed ?? 1;
  const color = managerColor(seed);

  return (
    <div className="pointer-events-none fixed inset-x-0 top-24 z-50 flex justify-center px-4">
      <div
        key={nonce}
        className="animate-announce flex flex-wrap items-center justify-center gap-x-4 gap-y-1 rounded-2xl border px-7 py-4 backdrop-blur"
        style={{
          borderColor: color,
          background: "rgba(10,14,20,0.92)",
          boxShadow: `0 0 0 1px ${color}55, 0 18px 55px rgba(0,0,0,0.6)`,
        }}
      >
        <span
          className="text-[10px] font-bold uppercase tracking-[0.3em]"
          style={{ color }}
        >
          Pick {show.pickNumber} is in
        </span>
        <span className="text-2xl font-black text-fg">{show.manager}</span>
        <span className="text-muted">drafts</span>
        <span className="num text-3xl font-black" style={{ color }}>
          {show.ticker}
        </span>
        <DirectionTag direction={show.direction} />
        <span className="num text-sm text-faint">{formatMoney(show.costBasis)}</span>
      </div>
    </div>
  );
}
